import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listRecalls, createRecall } from "@/lib/recalls.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/compliance/recalls/")({ component: RecallsPage });

function RecallsPage() {
  const { tenantId, customerId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listRecalls);
  const createFn = useServerFn(createRecall);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    skuId: "", lotCode: "", reason: "", severity: "voluntary" as "voluntary"|"mandatory"|"class_i"|"class_ii"|"class_iii",
    regulatorRef: "", notes: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["recalls", tenantId, customerId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId!, customerId: customerId ?? null, status: "all" } }),
    placeholderData: keepPreviousData,
  });

  const create = useMutation({
    mutationFn: () => createFn({ data: {
      tenantId: tenantId!, customerId: customerId!,
      skuId: form.skuId || null, lotCode: form.lotCode || null,
      reason: form.reason, severity: form.severity,
      regulatorRef: form.regulatorRef || null, notes: form.notes || null,
    } }),
    onSuccess: () => { toast.success("Recall created"); setOpen(false); qc.invalidateQueries({ queryKey: ["recalls"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader title="Recalls"
        description="Initiate, track, and close product recalls. Auto-quarantines matching lots and flags downstream shipments."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button disabled={!customerId} title={!customerId ? "Pick a customer scope first" : ""}>New recall</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New recall</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><label className="text-xs text-muted-foreground">SKU ID (optional)</label>
                  <Input value={form.skuId} onChange={(e) => setForm({ ...form, skuId: e.target.value })} placeholder="uuid"/></div>
                <div><label className="text-xs text-muted-foreground">Lot code</label>
                  <Input value={form.lotCode} onChange={(e) => setForm({ ...form, lotCode: e.target.value })}/></div>
                <div><label className="text-xs text-muted-foreground">Severity</label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v as any })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["voluntary","mandatory","class_i","class_ii","class_iii"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
                <div><label className="text-xs text-muted-foreground">Reason</label>
                  <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2}/></div>
                <div><label className="text-xs text-muted-foreground">Regulator reference</label>
                  <Input value={form.regulatorRef} onChange={(e) => setForm({ ...form, regulatorRef: e.target.value })}/></div>
                <div><label className="text-xs text-muted-foreground">Notes</label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}/></div>
              </div>
              <DialogFooter>
                <Button onClick={() => create.mutate()} disabled={create.isPending || !form.reason || form.reason.length < 3}>Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="px-4 sm:px-6 py-4">
        <div className="border rounded-md bg-card overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Initiated</TableHead><TableHead>Severity</TableHead><TableHead>Lot</TableHead>
              <TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead>Regulator</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!isLoading && (data ?? []).length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No recalls yet.</TableCell></TableRow>}
              {(data ?? []).map((r: any) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{new Date(r.initiated_at).toLocaleString()}</TableCell>
                  <TableCell><Badge variant={r.severity.startsWith("class") || r.severity === "mandatory" ? "destructive" : "secondary"}>{r.severity}</Badge></TableCell>
                  <TableCell className="font-mono text-xs">{r.lot_code ?? "—"}</TableCell>
                  <TableCell className="text-xs max-w-[280px] truncate">{r.reason}</TableCell>
                  <TableCell><Badge>{r.status}</Badge></TableCell>
                  <TableCell className="text-xs">{r.regulator_ref ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    <Link to="/app/compliance/recalls/$id" params={{ id: r.id }} className="text-primary text-sm hover:underline">Open →</Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}